import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

export default function ProductDetailModal({ product, selectedCurrency, price, onClose, onAddToCart }) {
  if (!product) return null;

  const [componentDetails, setComponentDetails] = useState({});
  const [loadingComponents, setLoadingComponents] = useState(false);

  // Google Drive image handling (same as Orders.jsx)
  const PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  const REGION = 'europe-west3';
  const FN_BASE = PROJECT_ID ? `https://${REGION}-${PROJECT_ID}.cloudfunctions.net` : '';

  const extractDriveFileId = (url) => {
    if (typeof url !== 'string') return '';
    const trimmed = url.trim();
    if (!trimmed) return '';

    const driveMatch = trimmed.match(/\/file\/d\/([^/]+)\//);
    if (driveMatch?.[1]) return driveMatch[1];

    const idParamMatch = trimmed.match(/[?&]id=([^&]+)/);
    if (idParamMatch?.[1]) return idParamMatch[1];

    return '';
  };

  const getImageSrc = (url) => {
    if (typeof url !== 'string') return '';
    const trimmed = url.trim();
    if (!trimmed) return '';

    // Handle Google Drive URLs
    const fileId = extractDriveFileId(trimmed);
    if (fileId && FN_BASE) {
      return `${FN_BASE}/serveDriveImage?fileId=${encodeURIComponent(fileId)}`;
    }

    // Return public folder URL as-is
    return trimmed;
  };

  const hasUSP = product.USP && product.USP.trim() !== '';
  const hasImage = product.imageUrl && product.imageUrl.trim() !== '';

  // Fetch component details when product changes
  useEffect(() => {
    const fetchComponentDetails = async () => {
      if (!product.components || product.components.trim() === '') {
        setComponentDetails({});
        return;
      }

      setLoadingComponents(true);
      const componentCodes = product.components.split(/[\s,]+/).filter(Boolean);
      const details = {};

      try {
        for (const code of componentCodes) {
          const componentDoc = await getDoc(doc(db, 'products', code.trim()));
          if (componentDoc.exists()) {
            details[code.trim()] = componentDoc.data();
          } else {
            // If component not found, still store the code for display
            details[code.trim()] = { 
              name: code.trim(), 
              code: code.trim(),
              notFound: true 
            };
          }
        }
        setComponentDetails(details);
      } catch (error) {
        console.error('Error fetching component details:', error);
        // On error, create basic entries for display
        componentCodes.forEach(code => {
          details[code.trim()] = { 
            name: code.trim(), 
            code: code.trim(),
            error: true 
          };
        });
        setComponentDetails(details);
      } finally {
        setLoadingComponents(false);
      }
    };

    fetchComponentDetails();
  }, [product.components]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#F7FBFC] rounded-xl shadow-2xl max-w-6xl max-h-[90vh] overflow-y-auto w-full border border-[#19BFB7]/20">
        {/* Header */}
        <div className="sticky top-0 bg-[#F7FBFC] border-b border-[#19BFB7]/20 p-4 sm:p-6 z-10">
          <div className="flex justify-between items-start sm:items-center">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-[#011841] mb-2">{product.name}</h2>
              {product.category && (
                <p className="text-sm text-[#033EA5] bg-white px-3 py-1 rounded-lg inline-block border border-[#19BFB7]/10">
                  📁 {product.category}
                </p>
              )}
            </div>
            <button 
              onClick={onClose}
              className="text-[#033EA5] hover:text-[#022b73] text-2xl font-bold p-2 rounded-lg hover:bg-white transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {/* Product Image */}
          <div className="flex justify-center mb-6">
            {hasImage ? (
              <img 
                src={getImageSrc(product.imageUrl)}
                alt={product.name}
                className="w-full h-auto max-h-96 object-contain rounded-lg shadow-lg bg-white sm:max-w-none md:max-w-2xl lg:max-w-3xl"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className="w-full h-64 sm:h-80 md:h-96 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-center px-4" style={{display: hasImage ? 'none' : 'flex'}}>
              <span className="text-sm font-medium text-gray-500">No image available</span>
            </div>
          </div>

          {/* Price */}
          <div className="text-center mb-6">
            <div className="text-2xl font-semibold text-[#033EA5] mb-2">
              {price}
            </div>
            {product.code && (
              <p className="text-sm text-gray-600">Product Code: {product.code}</p>
            )}
          </div>

          {/* USP Section - Full and Scrollable */}
          {hasUSP && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-[#011841] mb-4">✨ Key Features & Benefits</h3>
              <div className="bg-white rounded-lg p-4 sm:p-6 border border-[#19BFB7]/20 shadow-sm">
                <div className="max-h-64 overflow-y-auto pr-2">
                  <p className="text-gray-700 whitespace-pre-line leading-relaxed text-sm">
                    {product.USP}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Additional Product Information */}
          {product.description && (
            <div className="mb-6">
              <h3 className="text-base font-semibold text-[#011841] mb-3">📝 Description</h3>
              <div className="bg-white rounded-lg p-4 border border-[#19BFB7]/20">
                <p className="text-gray-700 leading-relaxed">{product.description}</p>
              </div>
            </div>
          )}

          {/* Product Components (if available) */}
          {product.components && product.components.trim() !== '' && (
            <div className="mb-6">
              <h3 className="text-base font-semibold text-[#011841] mb-3">🔧 Components</h3>
              <div className="bg-white rounded-lg p-4 border border-[#19BFB7]/20">
                {loadingComponents ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#19BFB7]"></div>
                    <span className="ml-2 text-gray-600">Loading component details...</span>
                  </div>
                ) : (
                  <ul className="space-y-4">
                    {product.components.split(/[\s,]+/).filter(Boolean).map((componentCode, index) => {
                      const trimmedCode = componentCode.trim();
                      const component = componentDetails[trimmedCode] || { 
                        name: trimmedCode, 
                        code: trimmedCode,
                        notFound: true 
                      };
                      
                      return (
                        <li key={index} className="flex items-start space-x-3 p-3 rounded-lg border border-gray-100 hover:border-[#19BFB7]/20 transition-colors">
                          {/* Component Image */}
                          <div className="flex-shrink-0">
                            {component.imageUrl && component.imageUrl.trim() !== '' ? (
                              <img 
                                src={getImageSrc(component.imageUrl)} 
                                alt={component.name || trimmedCode}
                                className="w-24 h-24 object-contain rounded-lg border border-gray-200 bg-white"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            {/* Fallback placeholder when no image or image fails to load */}
                            <div className={`w-24 h-24 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center ${component.imageUrl ? 'hidden' : 'flex'}`}>
                              <span className="text-gray-400 text-2xl">📦</span>
                            </div>
                          </div>
                          
                          {/* Component Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <p className="font-medium text-gray-800">{component.name || trimmedCode}</p>
                              {component.notFound && (
                                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">Not Found</span>
                              )}
                              {component.error && (
                                <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">Error</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-1">Code: {trimmedCode}</p>
                            {component.description && component.description.trim() !== '' && (
                              <p className="text-sm text-gray-700 leading-relaxed">{component.description}</p>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-[#F7FBFC] border-t border-[#19BFB7]/20 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <button 
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-[#19BFB7]/30 rounded-lg hover:bg-[#E8F4F8] text-[#033EA5] font-medium transition-all duration-300"
            >
              Close
            </button>
            <button 
              onClick={onAddToCart}
              className="flex-1 px-6 py-3 bg-[#033EA5] hover:bg-[#022b73] text-white rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg"
            >
              🛒 Add to Cart - {price}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
